import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import authService from '../services/authService';
import rateLimiter from '../services/rateLimiter';
import storageService from '../services/storageService'; // Import Secure Storage
import companyService from '../services/companyService';
import { ERROR_CODES } from '../services/apiConfig';
import axios from 'axios'; // Mantener por si acaso checkAuth lo usa aun

/**
 * ------------------------------------------------------------------
 * AUTH CONTEXT
 * ------------------------------------------------------------------
 * Maneja el estado de autenticación del usuario
 * - user: información del usuario autenticado
 * - login: función para autenticar usuario
 * - logout: función para cerrar sesión
 * - isAuthenticated: booleano indicando si hay usuario autenticado
 * ------------------------------------------------------------------
 */

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false); // Operaciones (login, checkUser, etc.)
    const [checkingAuth, setCheckingAuth] = useState(true); // Solo para validación inicial de sesión
    const [error, setError] = useState(null);

    /**
     * Verificar si hay una sesión activa al cargar la app
     */
    useEffect(() => {
        checkAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Limpiar sesión de forma centralizada
     */
    const clearSession = useCallback(() => {
        setLoading(true);
        setUser(null);
        storageService.clear(); // Limpiar todo lo seguro (user, token, company)
        setLoading(false);
    }, []);

    /**
     * Verifica si el usuario está autenticado
     */
    const checkAuth = useCallback(async () => {
        setCheckingAuth(true);
        try {
            // Cargar sesión desde storage seguro (ya viene desencriptado y parseado)
            const storedUser = storageService.getItem('user');
            const storedToken = storageService.getItem('token');
            const storedCompany = storageService.getItem('activeCompany');

            if (storedUser && storedToken) {
                // 1. Validar expiración localmente (Client-Side)
                try {
                    const parts = storedToken.split('.');
                    if (parts.length === 3) {
                        const payloadBase64 = parts[1];
                        const decodedJson = JSON.parse(atob(payloadBase64));
                        const exp = decodedJson.exp * 1000; // JWT exp is in seconds

                        // Verificar si ya expiró
                        if (Date.now() >= exp) {
                            console.warn('⚠️ Token expirado localmente. Limpiando items de sesión.');
                            clearSession();
                            return;
                        }
                    } else {
                        // El token no parece ser un JWT, asumimos que es un token opaco y lo validamos con el servidor
                        console.log('ℹ️ Token con formato no JWT detectado. Se validará con el servidor.');
                    }
                } catch (e) {
                    // Si falla el decode, no limpiamos sesión inmediatamente. Dejamos que el servidor decida.
                    console.warn('⚠️ No se pudo decodificar el token localmente (posible token opaco). Validando con servidor...');
                }

                // 2. PROBE: Verificar validez con el servidor (Solo si pasó la validación local)
                try {
                    await companyService.getAll();
                } catch (probeError) {
                    if (probeError.response && (probeError.response.status === ERROR_CODES.UNAUTHORIZED || probeError.response.status === ERROR_CODES.FORBIDDEN)) {
                        console.warn('⚠️ Token rechazado por servidor (401/403). Limpiando.');
                        clearSession();
                        return;
                    }
                    console.warn(`⚠️ Error en validación de token (server probe): ${probeError.message}. Se mantiene sesión offline/degradada.`);
                }

                console.log('✅ Sesión recuperada y validada (Secure Storage)');

                // storedUser y storedCompany ya son objetos gracias a storageService.getItem
                const parsedUser = storedUser;

                if (storedCompany) {
                    parsedUser.company = storedCompany;
                }

                setUser(parsedUser);
            } else {
                throw new Error('No session');
            }
        } catch (err) {
            if (err.message !== 'No session') {
                clearSession();
            } else {
                setLoading(false);
            }
        } finally {
            setCheckingAuth(false);
        }
    }, [clearSession]);


    /**
     * Paso 1: Verificar usuario y obtener empresas
     * @param {string} email
     */
    const checkUser = useCallback(async (email) => {
        setLoading(true);
        setError(null);

        // Verificar si el email está bloqueado
        const blockStatus = rateLimiter.isBlocked(email);
        if (blockStatus.blocked) {
            setLoading(false);
            return {
                success: false,
                error: `Demasiados intentos fallidos. Intente nuevamente en ${blockStatus.remainingTime} minutos.`,
                blocked: true,
                remainingTime: blockStatus.remainingTime
            };
        }

        try {
            const response = await authService.checkUser(email);
            return {
                success: true,
                user: response.user || response, // Adaptar según respuesta real
                companies: response.companies || []
            };
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Usuario no encontrado';
            setError(errorMessage);

            // Registrar intento fallido
            const limitResult = rateLimiter.recordFailedAttempt(email);

            if (limitResult.blocked) {
                return {
                    success: false,
                    error: `Demasiados intentos fallidos. Cuenta bloqueada por 30 minutos.`,
                    blocked: true
                };
            }

            return {
                success: false,
                error: errorMessage,
                remainingAttempts: limitResult.remainingAttempts
            };
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Paso 2: Login con empresa seleccionada
     */
    const login = useCallback(async (userId, companyId, password, email) => {
        setLoading(true);
        setError(null);

        try {
            const response = await authService.login({
                user_id: userId,
                company_id: companyId,
                password
            });

            const token = response.token || response.access_token;

            if (response.user && token) {
                // Guardar token, usuario y empresa (ENCRIPTADO)
                storageService.setItem('token', token);
                storageService.setItem('user', response.user);

                // IMPORTANTE: Persistir empresa para 'Offline Mode' o recargas
                if (response.company) {
                    storageService.setItem('activeCompany', response.company);
                }

                setUser({ ...response.user, company: response.company });

                // Limpiar intentos fallidos en login exitoso
                if (email) {
                    rateLimiter.clearAttempts(email);
                }

                return { success: true, user: response.user };
            } else {
                throw new Error('Respuesta inválida del servidor');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Credenciales incorrectas';
            const errorCode = err.response?.data?.code || 'LOGIN_ERROR';

            setError(errorMessage);
            setUser(null);

            // Registrar intento fallido si tenemos el email
            if (email) {
                const limitResult = rateLimiter.recordFailedAttempt(email);

                if (limitResult.blocked) {
                    return {
                        success: false,
                        error: `Demasiados intentos fallidos. Cuenta bloqueada por 30 minutos.`,
                        code: 'RATE_LIMIT_EXCEEDED',
                        blocked: true
                    };
                }
            }

            return {
                success: false,
                error: errorMessage,
                code: errorCode
            };
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Logout de usuario
     */
    const logout = useCallback(async () => {
        setLoading(true);
        try {
            await authService.logout();
        } catch (err) {
            console.error('Error al cerrar sesión:', err);
        } finally {
            clearSession();
            window.location.href = '/login';
        }
    }, [clearSession]);

    /**
     * Refrescar información del usuario actual
     */
    const refreshUser = async () => {
        try {
            const response = await axios.get('/api/auth/me', {
                withCredentials: true
            });

            if (response.data.ok && response.data.user) {
                setUser(response.data.user);
                return response.data.user;
            }
        } catch (err) {
            console.error('Error al refrescar usuario:', err);
            return null;
        }
    };

    /**
     * Verificar si el usuario tiene un rol específico
     * @param {string|Array<string>} roles - Rol o array de roles permitidos
     */
    const hasRole = useCallback((roles) => {
        if (!user) return false;

        if (Array.isArray(roles)) {
            return roles.includes(user.role);
        }

        return user.role === roles;
    }, [user]);

    /**
     * Verificar si el usuario es admin o userRoot
     */
    const isAdmin = useCallback(() => {
        return hasRole(['admin', 'userRoot']);
    }, [hasRole]);

    const value = useMemo(() => ({
        user,
        loading,
        checkingAuth, // Expose new state
        error,
        isAuthenticated: !!user,
        login,
        logout,
        checkAuth,
        checkUser, // Add checkUser to export
        refreshUser,
        hasRole,
        isAdmin
    }), [user, loading, checkingAuth, error, login, checkAuth, checkUser, hasRole, isAdmin, logout]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
